/**
 * Feb 27, 2026 · Excess Production
 *
 * Admin queue for Packing-Slip boxes that pushed their SO/ESO line
 * past pending. Every row shows the excess qty, the source SO/ESO, the
 * PS/box, and offers three resolutions:
 *   • Approve for Billing     — mark for extra invoicing
 *   • Move to General Stock   — absorb into free-stock inventory
 *   • Reject                  — with reason
 * Also exposes a per-fabric-type mini summary at the top matching the
 * dashboard tile.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FormattedNumberInput from "@/components/FormattedNumberInput";
import {
  StackIcon,
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  ProhibitIcon,
  PackageIcon,
  PlusIcon,
} from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const STATUS_CLS = {
  "Pending Approval":       "border-amber-500/50 text-amber-300 bg-amber-500/10",
  "Approved for Billing":   "border-emerald-500/50 text-emerald-300 bg-emerald-500/10",
  "Moved to General Stock": "border-sky-500/50 text-sky-300 bg-sky-500/10",
  "Rejected":               "border-red-500/50 text-red-300 bg-red-500/10",
};
const FILTERS = ["ALL", "Pending Approval", "Approved for Billing", "Moved to General Stock", "Rejected"];

export default function ExcessProduction() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totals: {}, by_fabric_type: [], by_status: [] });
  const [filter, setFilter] = useState("Pending Approval");
  const [loading, setLoading] = useState(false);
  // Feb 28, 2026 · Manual "New Excess Entry" dialog. Lets operators record
  // over-production that the auto-detector missed (or historical records
  // not linked to any Packing Slip). Feeds the same DB collection as the
  // auto entries so the queue + downstream dispatch flow don't need to
  // discriminate between the two sources.
  const emptyManual = () => ({
    source_type: "SO",
    order_id: "",
    product_code: "",
    style_no: "",
    fabric_type: "",
    excess_qty: 0,
    notes: "",
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState(emptyManual());
  const [manualSaving, setManualSaving] = useState(false);
  const [salesOrders, setSalesOrders] = useState([]);
  const [exportOrders, setExportOrders] = useState([]);

  useEffect(() => {
    // Load SO + ESO for the manual-entry order picker (once).
    Promise.all([api.get("/sales-orders/"), api.get("/exports/orders/")])
      .then(([s, e]) => { setSalesOrders(s.data || []); setExportOrders(e.data || []); })
      .catch(() => { /* noop — manual dialog will still open, just without SO options */ });
  }, []);

  // Line-item picker options driven by the picked order.
  const manualLineItems = useMemo(() => {
    const list = manualForm.source_type === "SO" ? salesOrders : exportOrders;
    const order = list.find(o => o.id === manualForm.order_id);
    if (!order) return [];
    const seen = new Set();
    return (order.items || []).filter(li => li.article_no).map(li => ({
      product_code: li.article_no,
      style_no: li.style_no || "",
      unit: li.unit || "meter",
    })).filter(o => { if (seen.has(o.product_code)) return false; seen.add(o.product_code); return true; });
  }, [manualForm.source_type, manualForm.order_id, salesOrders, exportOrders]);

  const submitManual = async () => {
    if (!manualForm.order_id)     return toast.error(`Pick a ${manualForm.source_type} order`);
    if (!manualForm.product_code) return toast.error("Pick the line item / product code");
    if (!(Number(manualForm.excess_qty) > 0)) return toast.error("Excess qty must be > 0");
    setManualSaving(true);
    try {
      const { data } = await api.post("/excess-production/", {
        ...manualForm,
        excess_qty: Number(manualForm.excess_qty),
      });
      toast.success(`Created ${data.entry_no} · Pending Approval`);
      setManualOpen(false);
      setManualForm(emptyManual());
      setFilter("Pending Approval");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setManualSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/excess-production/", { params: filter === "ALL" ? {} : { status: filter } }),
        api.get("/excess-production/summary"),
      ]);
      setRows(r || []);
      setSummary(s || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const act = async (r, kind) => {
    const label = { billing: "Approve for Billing", stock: "Move to General Stock", reject: "Reject" }[kind];
    const path  = { billing: "approve-billing",     stock: "move-to-stock",         reject: "reject"     }[kind];
    const notes = window.prompt(`${label} for ${r.entry_no}?${kind === "reject" ? "\nReason (min 4 chars, required):" : "\nOptional note:"}`, "");
    if (notes === null) return;
    if (kind === "reject" && (notes || "").trim().length < 4) { toast.error("Rejection reason must be at least 4 characters"); return; }
    try {
      await api.post(`/excess-production/${r.id}/${path}`, { notes });
      toast.success(`${r.entry_no} · ${label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const totalQty = useMemo(() => rows.reduce((s, r) => s + Number(r.excess_qty || 0), 0), [rows]);

  return (
    <div className="p-6 space-y-5" data-testid="excess-production-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><PackageIcon size={11} weight="bold" />Dispatch · Over-Production</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Excess Production Queue</h1>
          <div className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Packing-slip boxes that pushed their SO line past pending. Approve for extra billing or absorb into
            general stock, per fabric type.
          </div>
        </div>
        <button data-testid="ep-refresh" onClick={load} disabled={loading}
          className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2">
          <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
        </button>
        <button data-testid="ep-new-entry-btn" onClick={() => { setManualForm(emptyManual()); setManualOpen(true); }}
          className="h-10 px-4 bg-amber-500 hover:bg-amber-600 text-black text-sm font-semibold rounded-sm flex items-center gap-2">
          <PlusIcon size={14} weight="bold" />New Excess Entry
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ep-kpis">
        {[
          { label: "Total Entries",   value: summary.totals?.entries || 0, color: "text-white" },
          { label: "Pending Approval",value: summary.totals?.pending_entries || 0, sub: `${fmt(summary.totals?.pending_qty)} m`, color: "text-amber-300" },
          { label: "Total Excess Qty",value: `${fmt(summary.totals?.qty)}`, sub: "meters", color: "text-sky-300" },
          { label: "Fabric Types",    value: (summary.by_fabric_type || []).length, color: "text-purple-300" },
        ].map((k, i) => (
          <div key={i} className="tactical-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k.label}</div>
            <div className={`text-2xl font-mono mt-1 ${k.color}`}>{k.value}</div>
            {k.sub && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{k.sub}</div>}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button key={f} data-testid={`ep-filter-${f.replace(/ /g,"-").toLowerCase()}`} onClick={() => setFilter(f)}
            className={`h-8 px-3 text-xs font-mono uppercase tracking-widest rounded-sm border ${
              filter === f ? "border-[var(--accent)] bg-[var(--accent)]/20 text-white" : "border-[var(--border)] text-zinc-400 hover:bg-[var(--bg-2)]"
            }`}>{f}</button>
        ))}
        <div className="ml-auto text-[11px] font-mono uppercase tracking-widest text-zinc-500">
          {rows.length} rows · Total {fmt(totalQty)} m
        </div>
      </div>

      <div className="tactical-card overflow-x-auto" data-testid="ep-table">
        <table className="w-full text-xs">
          <thead className="bg-[var(--bg-2)]">
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-3 py-2">Entry No</th>
              <th className="text-left px-3 py-2">PS · Box</th>
              <th className="text-left px-3 py-2">Order</th>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2">Fabric</th>
              <th className="text-right px-3 py-2">Packed</th>
              <th className="text-right px-3 py-2">Excess</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-center px-3 py-2 w-52">—</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-6 text-zinc-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-zinc-500">No excess entries in this view — production is on target.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} data-testid={`ep-row-${r.id}`} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                <td className="px-3 py-2 font-mono text-[var(--accent)]">{r.entry_no}</td>
                <td className="px-3 py-2 text-[10px] font-mono text-zinc-400">{r.slip_no}<div className="text-zinc-600">{r.box_no}</div></td>
                <td className="px-3 py-2 text-zinc-300">{r.order_no}<div className="text-[9px] font-mono text-zinc-600">{r.source_type}</div></td>
                <td className="px-3 py-2 text-zinc-300 truncate max-w-[220px]" title={r.product_code}>{r.product_code}</td>
                <td className="px-3 py-2 text-purple-300">{r.fabric_type}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmt(r.packed_qty)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300">{fmt(r.excess_qty)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-widest ${STATUS_CLS[r.status] || ""}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-center">
                  {r.status === "Pending Approval" ? (
                    <div className="flex items-center justify-center gap-1">
                      <button data-testid={`ep-billing-${r.id}`} onClick={() => act(r, "billing")}
                        className="h-7 px-2 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 rounded-sm text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1">
                        <CheckCircleIcon size={11} weight="bold" />Bill
                      </button>
                      <button data-testid={`ep-stock-${r.id}`} onClick={() => act(r, "stock")}
                        className="h-7 px-2 border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 rounded-sm text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1">
                        <StackIcon size={11} weight="bold" />Stock
                      </button>
                      <button data-testid={`ep-reject-${r.id}`} onClick={() => act(r, "reject")}
                        className="h-7 px-2 border border-red-500/50 text-red-300 hover:bg-red-500/10 rounded-sm text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1">
                        <ProhibitIcon size={11} weight="bold" />Reject
                      </button>
                    </div>
                  ) : (
                    <div className="text-[9px] font-mono text-zinc-500">
                      by {r.resolved_by || "—"}
                      <div className="text-zinc-600">{(r.resolved_at || "").slice(0, 10)}</div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="tactical-card" data-testid="ep-fabric-breakdown">
        <div className="px-4 py-2 border-b border-[var(--border)] text-[11px] font-mono uppercase tracking-widest text-zinc-400">By Fabric Type</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]">
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-3 py-2">Fabric Type</th>
                <th className="text-right px-3 py-2">Entries</th>
                <th className="text-right px-3 py-2">Pending</th>
                <th className="text-right px-3 py-2">Total Qty</th>
              </tr>
            </thead>
            <tbody>
              {(summary.by_fabric_type || []).length === 0 && <tr><td colSpan={4} className="text-center py-6 text-zinc-500">No excess by fabric on record.</td></tr>}
              {(summary.by_fabric_type || []).map(r => (
                <tr key={r.fabric_type} className="border-b border-[var(--border)]">
                  <td className="px-3 py-2 text-purple-300">{r.fabric_type}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{r.count}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300">{r.pending}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-sky-300">{fmt(r.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Feb 28, 2026 · Manual "New Excess Entry" dialog. Same collection +
          approval workflow as auto-generated entries, but no PS linkage. */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl" data-testid="ep-manual-dialog" description="Excess production action — reallocate or dispose extra metres over SO target.">
          <DialogHeader><DialogTitle className="font-display">New Excess Entry · Manual</DialogTitle></DialogHeader>
          <div className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
            Record over-production that isn't tied to a Packing Slip (e.g. historical adjustments, QC-found surplus,
            or auto-detector misses). Entries land in <span className="text-amber-300 font-semibold">Pending Approval</span> —
            approve them here to make them dispatch-ready.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1.5">Source</div>
              <Select value={manualForm.source_type} onValueChange={v => setManualForm(f => ({ ...f, source_type: v, order_id: "", product_code: "" }))}>
                <SelectTrigger data-testid="ep-manual-source" className="bg-[var(--bg-0)] border-[var(--border)] h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  <SelectItem value="SO">Sales Order (SO)</SelectItem>
                  <SelectItem value="ESO">Export Sales Order (ESO)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="overline mb-1.5">{manualForm.source_type === "SO" ? "Sales Order" : "Export Sales Order"}</div>
              <Select value={manualForm.order_id} onValueChange={v => setManualForm(f => ({ ...f, order_id: v, product_code: "", style_no: "" }))}>
                <SelectTrigger data-testid="ep-manual-order" className="bg-[var(--bg-0)] border-[var(--border)] h-9 text-sm"><SelectValue placeholder={`Pick an ${manualForm.source_type}…`} /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {(manualForm.source_type === "SO" ? salesOrders : exportOrders).map(o => (
                    <SelectItem key={o.id} value={o.id} data-testid={`ep-manual-order-opt-${o.order_no || o.doc_no}`}>
                      {o.order_no || o.doc_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="overline mb-1.5">Line Item / Product Code</div>
              <Select value={manualForm.product_code} onValueChange={v => {
                const li = manualLineItems.find(x => x.product_code === v);
                setManualForm(f => ({ ...f, product_code: v, style_no: li?.style_no || f.style_no }));
              }}>
                <SelectTrigger data-testid="ep-manual-line" className="bg-[var(--bg-0)] border-[var(--border)] h-9 text-sm"><SelectValue placeholder={manualForm.order_id ? "Pick a line item…" : "Pick an order first"} /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {manualLineItems.map(li => (
                    <SelectItem key={li.product_code} value={li.product_code} data-testid={`ep-manual-line-opt-${li.product_code}`}>
                      <span className="font-mono">{li.product_code}</span>
                      {li.style_no && <span className="text-zinc-500 ml-2">· {li.style_no}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="overline mb-1.5">Style No</div>
              <input data-testid="ep-manual-style" value={manualForm.style_no}
                onChange={e => setManualForm(f => ({ ...f, style_no: e.target.value }))}
                placeholder="Auto-filled from line item" className="w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] font-mono" />
            </div>
            <div>
              <div className="overline mb-1.5">Fabric Type</div>
              <input data-testid="ep-manual-fabric" value={manualForm.fabric_type}
                onChange={e => setManualForm(f => ({ ...f, fabric_type: e.target.value }))}
                placeholder="e.g. Elastic / Jacquard / Knitted" className="w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
            </div>
            <div>
              <div className="overline mb-1.5">Excess Qty (meters)</div>
              <FormattedNumberInput data-testid="ep-manual-qty" step="0.01" value={manualForm.excess_qty}
                onChange={e => setManualForm(f => ({ ...f, excess_qty: e.target.value }))}
                className="w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] text-right font-mono" />
            </div>
            <div className="col-span-2">
              <div className="overline mb-1.5">Notes (optional)</div>
              <textarea data-testid="ep-manual-notes" value={manualForm.notes}
                onChange={e => setManualForm(f => ({ ...f, notes: e.target.value }))}
                rows={2} placeholder="Why is this being recorded manually? (audit trail)"
                className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
            </div>
          </div>

          <DialogFooter className="mt-3">
            <button onClick={() => setManualOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="ep-manual-save" onClick={submitManual} disabled={manualSaving}
              className="px-4 py-2 rounded-sm bg-amber-500 hover:bg-amber-600 text-black text-sm font-semibold disabled:opacity-60">
              {manualSaving ? "Creating…" : "Create Excess Entry"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
